<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import DashboardAlertBadge from '@/inventoryDashboardModule/components/DashboardAlertBadge.vue';
import { DashboardAlert } from '@/inventoryDashboardModule/domains/entities/DashboardAlert';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';

const t = i18n.t;

const props = defineProps<{
  alerts: DashboardAlert[];
  severity: 'warning' | 'critical';
}>();

const containerRef = ref<HTMLElement | null>(null);
const measureRef = ref<HTMLElement | null>(null);
const isReasonShown = ref(true);

let resizeObserver: ResizeObserver | undefined;

/**
 * アラート根拠の表示メトリクス
 */
type AlertReasonMetric = {
  label?: string;
  value: string;
  isSubtle?: boolean;
  prefix?: string;
};

/**
 * 数値を表示用に変換する
 * @param value - 変換する数値
 */
const convertNumberToDisplayValue = (value: number | undefined) => value?.addComma() ?? '0';

/**
 * アラート根拠を横並び表示用のメトリクスに変換する
 * @param alert - 変換するアラート情報
 */
const convertAlertReasonToMetrics = (alert: DashboardAlert): AlertReasonMetric[] => {
  const reason = alert.reason;
  if (!reason) return [];

  const targetYearMonth = `(${DateService.convertIYearMonthToYYYYMMWithSlash(reason.targetYearMonth)})`;

  if ('latestCustomerForecastAmount' in reason) {
    return [
      {
        label: t('内示'),
        value: `${convertNumberToDisplayValue(reason.previousCustomerForecastAmount)} → ${convertNumberToDisplayValue(
          reason.latestCustomerForecastAmount
        )}`,
      },
      { value: targetYearMonth, isSubtle: true },
    ];
  }

  if ('salesResultAmount' in reason) {
    return [
      { label: t('内示'), value: convertNumberToDisplayValue(reason.customerForecastAmount) },
      { label: t('販売実績'), value: convertNumberToDisplayValue(reason.salesResultAmount), prefix: '→' },
      { value: targetYearMonth, isSubtle: true },
    ];
  }

  return [];
};

/**
 * アラート根拠メトリクスをテキストに変換する
 * @param metric - 変換するアラート根拠メトリクス
 */
const convertMetricToText = (metric: AlertReasonMetric) => {
  const body = metric.label ? `${metric.label} ${metric.value}` : metric.value;
  return metric.prefix ? `${metric.prefix} ${body}` : body;
};

/**
 * アラート根拠のtooltipテキストを生成する
 * @param alert - tooltipを表示するアラート情報
 */
const createTooltipText = (alert: DashboardAlert): string | undefined => {
  if (isReasonShown.value || !alert.reason) return undefined;

  return convertAlertReasonToMetrics(alert).map(convertMetricToText).join(' ');
};

/**
 * 表示状態を更新する
 */
const updateReasonVisibility = () => {
  const container = containerRef.value;
  const measure = measureRef.value;
  if (!container || !measure) return;

  isReasonShown.value = measure.scrollWidth <= container.clientWidth;
};

onMounted(async () => {
  await nextTick();
  updateReasonVisibility();

  resizeObserver = new ResizeObserver(updateReasonVisibility);
  if (containerRef.value) resizeObserver.observe(containerRef.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

watch(
  () => props.alerts,
  async () => {
    await nextTick();
    updateReasonVisibility();
  },
  { deep: true }
);
</script>

<template>
  <div class="alert-reason-list-wrap">
    <div ref="containerRef" class="alert-reason-list">
      <div v-for="alert in alerts" :key="alert.kind" v-tooltip.top="createTooltipText(alert)" class="alert-reason-list__item">
        <DashboardAlertBadge :alert="alert.kind" :severity="severity" />
        <div v-if="isReasonShown" class="alert-reason-list__metrics">
          <span
            v-for="metric in convertAlertReasonToMetrics(alert)"
            :key="`${alert.kind}-${metric.label ?? metric.value}`"
            class="alert-reason-list__metric"
            :class="{ 'alert-reason-list__metric--subtle': metric.isSubtle }"
          >
            <span v-if="metric.prefix" class="alert-reason-list__metric-prefix">{{ metric.prefix }}</span>
            <span v-if="metric.label" class="alert-reason-list__metric-label">{{ metric.label }}</span>
            <span class="alert-reason-list__metric-value">{{ metric.value }}</span>
          </span>
        </div>
      </div>
    </div>

    <div ref="measureRef" class="alert-reason-list alert-reason-list--measure" aria-hidden="true">
      <div v-for="alert in alerts" :key="alert.kind" class="alert-reason-list__item">
        <DashboardAlertBadge :alert="alert.kind" :severity="severity" />
        <div class="alert-reason-list__metrics">
          <span
            v-for="metric in convertAlertReasonToMetrics(alert)"
            :key="`${alert.kind}-${metric.label ?? metric.value}`"
            class="alert-reason-list__metric"
            :class="{ 'alert-reason-list__metric--subtle': metric.isSubtle }"
          >
            <span v-if="metric.prefix" class="alert-reason-list__metric-prefix">{{ metric.prefix }}</span>
            <span v-if="metric.label" class="alert-reason-list__metric-label">{{ metric.label }}</span>
            <span class="alert-reason-list__metric-value">{{ metric.value }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.alert-reason-list-wrap {
  position: relative;
  min-width: 0;
  width: 100%;
}

.alert-reason-list {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;

  &__item {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  &__metrics {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  &__metric {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    align-items: baseline;
    gap: 3px;
    min-width: 0;
    white-space: nowrap;

    &--subtle {
      .alert-reason-list__metric-value {
        font-weight: 400;
        color: $textSecondary;
      }
    }
  }

  &__metric-prefix {
    color: $textSecondary;
    font-size: 12px;
    line-height: 1.5;
    font-weight: 700;
    white-space: nowrap;
  }

  &__metric-label {
    color: $textSecondary;
    font-size: 11px;
    line-height: 1.5;
    white-space: nowrap;
  }

  &__metric-value {
    color: $textDefault;
    font-size: 12px;
    line-height: 1.5;
    font-weight: 700;
    white-space: nowrap;
  }

  &--measure {
    position: absolute;
    visibility: hidden;
    pointer-events: none;
    inset: 0 auto auto 0;
    width: max-content;
  }
}
</style>
